import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Calendar,
  ShoppingBag,
  Users,
  Settings,
  ShoppingCart,
  FileText,
  CreditCard,
  LogOut,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { toast } from 'sonner';

import EventsTab from '@/components/group/EventsTab';
import StoreTab from '@/components/group/StoreTab';
import FeedTab from '@/components/group/FeedTab';
import DirectoryTab from '@/components/group/DirectoryTab';
import CartDrawer from '@/components/group/CartDrawer';
import { placeholderImg } from '@/utils/ui';
import { useTranslation } from 'react-i18next';

const GroupSpace = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [group, setGroup] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cart state
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Subscription state
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/my-role`),
          api.get(`/groups/${groupId}/events`),
          api.get(`/groups/${groupId}/products`),
          api.get(`/groups/${groupId}/directory`),
        ]);

        const [groupRes, roleRes, eventsRes, productsRes, directoryRes] = results;

        if (groupRes.status === 'fulfilled' && alive) setGroup(groupRes.value.data);
        if (roleRes.status === 'fulfilled' && alive) setMyRole(roleRes.value.data);

        if (alive) {
          setEvents(eventsRes.status === 'fulfilled' ? eventsRes.value.data : []);
          setProducts(productsRes.status === 'fulfilled' ? productsRes.value.data : []);
          setDirectory(directoryRes.status === 'fulfilled' ? directoryRes.value.data : []);
        }

        if (groupRes.status === 'rejected') throw new Error('group fetch failed');
      } catch (e) {
        toast.error(
          e?.response?.data?.detail ||
            t('groupSpace.toasts.loadError', { defaultValue: 'Failed to load group data' })
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [groupId, t]);

  // Events (kept for compatibility; EventsTab uses its own purchase drawer)
  const handleJoinEvent = async (ev) => {
    try {
      const price = Number(ev?.price || 0);
      if (price > 0) {
        const { data } = await api.post(`/checkout/event`, { eventId: ev.id });
        window.location.href = data.url;
        return;
      }
      await api.post(`/events/${ev.id}/register`);
      toast.success(
        t('groupSpace.toasts.registerSuccess', { defaultValue: 'Registered for event!' })
      );
      const { data } = await api.get(`/groups/${groupId}/events`);
      setEvents(data);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupSpace.toasts.registerError', { defaultValue: 'Failed to register' })
      );
    }
  };

  // Store
  const handleBuyNow = async (product, quantity = 1) => {
    try {
      if (product?.stock === 0) return;
      const { data } = await api.post(`/checkout/product`, {
        productId: product.id,
        quantity,
      });
      window.location.href = data.url;
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupSpace.toasts.checkoutError', { defaultValue: 'Failed to start checkout' })
      );
    }
  };

  const addToCart = (product, qty = 1) => {
    if (!product) return;
    const q = Math.max(1, Number(qty || 1));
    setCart((prev) => {
      const idx = prev.findIndex((it) => it.product.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        const newQty = next[idx].quantity + q;
        const maxQty =
          product.stock != null ? Math.min(newQty, Number(product.stock)) : newQty;
        next[idx] = { ...next[idx], quantity: maxQty };
        return next;
      }
      const capped = product.stock != null ? Math.min(q, Number(product.stock)) : q;
      return [...prev, { product, quantity: capped }];
    });
    toast.success(t('groupSpace.toasts.cartAdded', { defaultValue: 'Added to cart' }));
  };

  // Cart ops
  const removeFromCart = (productId) =>
    setCart((prev) => prev.filter((it) => it.product.id !== productId));

  const setCartQty = (productId, qty) =>
    setCart((prev) =>
      prev.map((it) => {
        if (it.product.id !== productId) return it;
        const q = Math.max(1, Number(qty || 1));
        const max = it.product.stock != null ? Math.min(q, Number(it.product.stock)) : q;
        return { ...it, quantity: max };
      })
    );

  const incQty = (productId) =>
    setCart((prev) =>
      prev.map((it) => {
        if (it.product.id !== productId) return it;
        const next = it.quantity + 1;
        const max = it.product.stock != null ? Math.min(next, Number(it.product.stock)) : next;
        return { ...it, quantity: max };
      })
    );

  const decQty = (productId) =>
    setCart((prev) =>
      prev.map((it) => {
        if (it.product.id !== productId) return it;
        return { ...it, quantity: Math.max(1, it.quantity - 1) };
      })
    );

  const handleCheckoutCart = async () => {
    try {
      if (!cart.length) return;
      setCheckoutLoading(true);
      const items = cart.map((it) => ({
        productId: it.product.id,
        quantity: it.quantity,
      }));
      const { data } = await api.post(`/checkout/cart`, { items });
      window.location.href = data.url;
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupSpace.toasts.cartCheckoutError', {
            defaultValue: 'Failed to start cart checkout',
          })
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Subscription checkout
  const handleStartSubscription = async () => {
    try {
      setSubscribing(true);
      const { data } = await api.post('/checkout/membership', { groupId });

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      toast.success(
        t('groupSpace.toasts.subscriptionActivated', { defaultValue: 'Subscription activated' })
      );

      const [roleRes, groupRes] = await Promise.allSettled([
        api.get(`/groups/${groupId}/my-role`),
        api.get(`/groups/${groupId}`),
      ]);
      if (roleRes.status === 'fulfilled') setMyRole(roleRes.value.data);
      if (groupRes.status === 'fulfilled') setGroup(groupRes.value.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupSpace.toasts.subscriptionError', { defaultValue: 'Failed to start subscription' })
      );
    } finally {
      setSubscribing(false);
    }
  };

  // ✅ Leave group (member action)
  const handleLeaveGroup = async () => {
    const role = myRole?.role;

    if (!myRole?.is_member) {
      toast.error(
        t('groupSpace.settings.notMember', { defaultValue: 'You are not a member of this group.' })
      );
      return;
    }

    if (role === 'owner') {
      toast.error(
        t('groupSpace.settings.ownerCantLeave', { defaultValue: "The owner can't leave the group." })
      );
      return;
    }

    const ok = window.confirm(
      t('groupSpace.settings.leaveConfirm', {
        defaultValue:
          'Are you sure you want to leave this group? You will lose access immediately.',
      })
    );
    if (!ok) return;

    try {
      await api.post(`/groups/${groupId}/leave`);
      toast.success(t('groupSpace.settings.leaveSuccess', { defaultValue: 'You have left the group.' }));
      navigate('/dashboard');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupSpace.settings.leaveError', { defaultValue: 'Failed to leave the group.' })
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <p className="text-slate-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const role = myRole?.role;
  const isMember = !!myRole?.is_member;
  const membershipStatus = myRole?.membership_status;
  const isSubRequired = !!group?.require_subscription;
  const membershipPrice = Number(group?.membership_price || 0);
  const groupCurrency = (group?.currency || 'USD').toUpperCase();

  const hasOptionalSubscription = !isSubRequired && membershipPrice > 0;

  // Treat any member of a subscription-only group as "subscribed"
  const membershipActive = (isSubRequired && isMember) || membershipStatus === 'active';

  const canAdmin = role === 'owner' || role === 'admin';

  const cartCount = cart.reduce((n, it) => n + Number(it.quantity || 0), 0);

  // Header subscribe button visibility
  const showHeaderSubscribe =
    (isSubRequired && !membershipActive && !isMember) ||
    (hasOptionalSubscription && (!membershipActive || !isMember));

  const headerSubscribeLabel = membershipActive
    ? t('groupSpace.header.subscribed')
    : subscribing
    ? t('groupSpace.header.subscribing')
    : t('groupSpace.header.subscribe');

  const headerSubscribeDisabled = membershipActive || subscribing;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-2" />
            {t('groupSpace.header.back')}
          </Button>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              <img
                src={
                  group?.logo ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(group?.name || '')}&size=80`
                }
                alt={group?.name}
                className="w-16 h-16 rounded-lg object-cover"
                onError={(e) => {
                  const img = e.target;
                  img.src = placeholderImg;
                }}
              />
              <div>
                <h1 className="text-3xl font-bold text-slate-900" data-testid="group-space-title">
                  {group?.name}
                </h1>
                <p className="text-slate-600">
                  {t('groupSpace.members', {
                    count: group?.member_count || 0,
                    defaultValue: '{{count}} members',
                  })}
                </p>

                {isSubRequired && (
                  <p className="text-xs mt-1 text-amber-700 font-medium">
                    {t('groupSpace.subscription.requiredLabel', {
                      defaultValue: 'Subscription required to access this group',
                    })}
                  </p>
                )}

                {hasOptionalSubscription && !isSubRequired && (
                  <p className="text-xs mt-1 text-slate-600">
                    {t('groupSpace.subscription.optionalLabel', {
                      currency: groupCurrency,
                      price: membershipPrice.toFixed(2),
                      defaultValue: 'Optional membership available for {{currency}} {{price}}/year.',
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {canAdmin && (
                <Button
                  onClick={() => navigate(`/groups/${groupId}/admin`)}
                  variant="outline"
                  data-testid="admin-panel-btn"
                >
                  <Settings size={16} className="mr-2" />
                  {t('groupSpace.adminPanel', { defaultValue: 'Admin Panel' })}
                </Button>
              )}

              {/* Header subscribe button */}
              {showHeaderSubscribe && (
                <Button
                  variant={membershipActive ? 'outline' : 'default'}
                  onClick={membershipActive ? undefined : handleStartSubscription}
                  disabled={headerSubscribeDisabled}
                  className="hidden sm:inline-flex"
                  data-testid="header-subscribe-btn"
                >
                  <CreditCard size={16} className="mr-2" />
                  {headerSubscribeLabel}
                </Button>
              )}

              {/* Cart button with badge */}
              <Button
                variant="outline"
                className="relative"
                onClick={() => setCartOpen(true)}
                aria-label={t('groupSpace.cart.label', { defaultValue: 'Open cart' })}
              >
                <ShoppingCart size={18} className="mr-2" />
                {t('groupSpace.cart.labelShort', { defaultValue: 'Cart' })}
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Paywall */}
        {isSubRequired && !isMember ? (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>
                {t('groupSpace.subscription.title', { defaultValue: 'Subscription required' })}
              </CardTitle>
              <CardDescription>
                {t('groupSpace.subscription.description', {
                  defaultValue:
                    'This group is reserved for subscribed members. Activate your subscription to access the feed, events, store, and member directory.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-lg p-3 bg-slate-50">
                <p className="text-sm text-slate-600">
                  {t('groupSpace.subscription.yearlyFeeLabel', { defaultValue: 'Yearly membership fee' })}
                </p>
                <p className="text-xl font-semibold mt-1">
                  {membershipPrice > 0 ? (
                    <>
                      {groupCurrency} {membershipPrice.toFixed(2)}/
                      {t('groupSpace.subscription.perYear', { defaultValue: 'year' })}
                    </>
                  ) : (
                    <span className="text-emerald-600">
                      {t('groupSpace.subscription.freeLabel', {
                        defaultValue: 'Free (subscription still required to join)',
                      })}
                    </span>
                  )}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="flex-1" onClick={handleStartSubscription} disabled={subscribing}>
                  <CreditCard size={16} className="mr-2" />
                  {subscribing
                    ? t('groupSpace.subscription.starting', { defaultValue: 'Starting subscription…' })
                    : t('groupSpace.subscription.subscribeAndEnter', { defaultValue: 'Subscribe & Enter Group' })}
                </Button>
                <Button type="button" variant="outline" className="flex-1" onClick={() => navigate('/dashboard')}>
                  {t('groupSpace.header.back')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Tabs defaultValue="events" className="w-full">
              {/* 5 tabs including Settings */}
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 mb-6">
                <TabsTrigger value="feed">
                  <FileText size={16} className="mr-2" />
                  {t('groupSpace.tabs.feed')}
                </TabsTrigger>

                <TabsTrigger value="events">
                  <Calendar size={16} className="mr-2" />
                  {t('groupSpace.tabs.events')}
                </TabsTrigger>

                <TabsTrigger value="store">
                  <ShoppingBag size={16} className="mr-2" />
                  {t('groupSpace.tabs.store')}
                </TabsTrigger>

                <TabsTrigger value="directory">
                  <Users size={16} className="mr-2" />
                  {t('groupSpace.tabs.directory')}
                </TabsTrigger>

                <TabsTrigger value="settings">
                  <Settings size={16} className="mr-2" />
                  {t('groupSpace.tabs.settings', { defaultValue: 'Settings' })}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="feed">
                <FeedTab groupId={groupId} />
              </TabsContent>

              <TabsContent value="events">
                <EventsTab events={events} groupCurrency={groupCurrency} onJoinEvent={handleJoinEvent} />
              </TabsContent>

              <TabsContent value="store">
                <StoreTab
                  products={products}
                  groupCurrency={groupCurrency}
                  onAddToCart={addToCart}
                  onBuyNow={handleBuyNow}
                />
              </TabsContent>

              <TabsContent value="directory">
                <DirectoryTab directory={directory} />
              </TabsContent>

              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('groupSpace.settings.title', { defaultValue: 'Group settings' })}</CardTitle>
                    <CardDescription>
                      {t('groupSpace.settings.subtitle', {
                        defaultValue: 'Manage your membership and access to this group.',
                      })}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="border rounded-lg p-4 bg-slate-50">
                      <p className="text-sm text-slate-600">
                        {t('groupSpace.settings.roleLabel', { defaultValue: 'Your role' })}
                      </p>
                      <p className="font-semibold mt-1">{role || '—'}</p>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-semibold">
                            {t('groupSpace.settings.leaveTitle', { defaultValue: 'Leave this group' })}
                          </p>
                          <p className="text-sm text-slate-600">
                            {t('groupSpace.settings.leaveDescription', {
                              defaultValue:
                                'You will lose access to the group feed, events, store, and directory.',
                            })}
                          </p>
                        </div>

                        <Button
                          variant="destructive"
                          onClick={handleLeaveGroup}
                          disabled={!isMember || role === 'owner'}
                          className="shrink-0"
                        >
                          <LogOut size={16} className="mr-2" />
                          {t('groupSpace.settings.leaveButton', { defaultValue: 'Leave group' })}
                        </Button>
                      </div>

                      {role === 'owner' && (
                        <p className="text-xs text-amber-700 mt-3">
                          {t('groupSpace.settings.ownerHint', {
                            defaultValue:
                              "You're the owner of this group. Transfer ownership or delete the group before leaving.",
                          })}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      <CartDrawer
        open={cartOpen}
        onOpenChange={setCartOpen}
        cart={cart}
        currency={groupCurrency}
        onIncQty={incQty}
        onDecQty={decQty}
        onSetQty={setCartQty}
        onRemove={removeFromCart}
        onCheckout={handleCheckoutCart}
        checkoutLoading={checkoutLoading}
      />
    </div>
  );
};

export default GroupSpace;
